
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Classable, ContentEntity, HasChildren, Page, Shapeable, ContentNode as TContentNode } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { applyOpenGraphThumbnailSize, classNames } from '@shapeable/utils';
import { CellLabel, ComponentPropsProvider, ContentEntityRichTitle, EntityGrid, FeedEntryCard, GptButton, LinkButton, useAuth, useFeedEntries, useLang, useLink, useModal, usePosts, WordWriterEffect } from '@shapeable/ui';
import { sortBy } from 'lodash';
import { AQUA, ICAA } from '../../theme';
const cls = classNames('explorer-home-view-layout');

// -------- Types -------->

// -------- Props -------->

export type ExplorerHomeViewLayoutProps = Classable & HasChildren & { 
  entity?: Page;
};

export const ExplorerHomeViewLayoutDefaultProps: Omit<ExplorerHomeViewLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(5)};
  `,
});

const ContentStyles = breakpoints({
  base: css`
    padding-bottom: ${theme.UNIT(5)};
  `,
});

const SectionStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(8)};
    margin-bottom: ${theme.UNIT(4)};
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(3)};
  `,
});

const GptSectionStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(8)};
    margin-bottom: ${theme.UNIT(4)};
    display: flex;
    align-items: flex-start;
  `,
});

const PageCardsStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(3)};
    margin-bottom: ${theme.UNIT(3)};
  `,
});

const TitleAndIntroStyles = breakpoints({
  base: css`
    
  `,
});

const IntroStyles = breakpoints({
  base: css`
    font-size: 1.4em;
    line-height: 1.2;
    font-weight: 200;
    font-family: ${theme.FONT('copy')};
    color: ${theme.COLOR('text-mid')};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    font-weight: 300;
    margin-bottom: ${theme.UNIT(4)};
    line-height: 1.10;
    font-size: 1.8em;
    color: ${theme.COLOR('strong')};
  `,
});

const ContentLabelStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
    font-weight: 300;
  `,
});


const AiGlowShift = keyframes`
  0% {
    background-position: 12% 50%;
  }
  50% {
    background-position: 88% 50%;
  }
  100% {
    background-position: 12% 50%;
  }
`;

const AiIconPulse = keyframes`
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.04);
  }
`;

const GptButtonStyles = breakpoints({
  base: css`
    ${theme.TRANSITION('box-shadow', 'transform')};
    border-radius: 100px;
    position: relative;
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    box-shadow: 0 6px 8px ${ICAA}35;

    .shp--icon-text-value {
      min-width: 140px;
      border-radius: 100px;
      background: linear-gradient(120deg, #fff6f2 0%, ${ICAA}68 18%, ${ICAA}4f 38%, ${ICAA}36 56%, #e0f6f6 70%, ${AQUA}34 82%, ${ICAA}56 100%);
      background-size: 280% 280%;
      animation: ${AiGlowShift} 7.25s ease-in-out infinite;
      box-shadow: 0 2px 8px ${ICAA}22;
      ${theme.TRANSITION('box-shadow')};
    }

    .shp--icon-text-value .shp--icon {
      ${theme.FILL('strong')};
      transform-origin: center;
      ${theme.TRANSITION('color', 'scale')};
    }

    &:hover {
      ${theme.FILL('primary')};
      transform: scale(1.01);
      transform-origin: center center;

      .shp--icon-text-value {
        box-shadow: 0 0 0 1px ${AQUA}2e, 0 10px 18px ${ICAA}30;
      }

      .shp--icon-text-value .shp--icon {
        ${theme.FILL('button-hover')};
        animation: ${AiIconPulse} 1.25s ease-in-out infinite;
      }
    }
  `,
});

const FeaturedNewsStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(4)};
  `,
});

const FeaturedNewsCardStyles = breakpoints({
  base: css`
    
  `,
});

const DiscoverButtonStyles = breakpoints({
  base: css`
    max-width: 300px;
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,

  PageCards: styled(EntityGrid)`${PageCardsStyles}`,
    TitleAndIntro: styled.div`${TitleAndIntroStyles}`,
    Title: styled(ContentEntityRichTitle)`${TitleStyles}`,
    Intro: styled(WordWriterEffect)`${IntroStyles}`,

  Content: styled.div`${ContentStyles}`,
    Section: styled.section`${SectionStyles}`,
    GptSection: styled.section`${GptSectionStyles}`,
      ContentLabel: styled(CellLabel)`${ContentLabelStyles}`,
      GptButton: styled(GptButton)`${GptButtonStyles}`,
      FeaturedNews: styled.div`${FeaturedNewsStyles}`,
      FeaturedNewsCard: styled(FeedEntryCard)`${FeaturedNewsCardStyles}`,

    DiscoverButton: styled(LinkButton)`${DiscoverButtonStyles}`,
};

export const ExplorerHomeViewLayout: Shapeable.FC<ExplorerHomeViewLayoutProps> = (props) => {
  const { className, children, entity } = props;

  const { cardLinks = [] } = entity;

  const auth = useAuth();
  const { navigate } = useLink();

  const userFirstName = auth?.user.name.split(' ')[0];
  
  const t = useLang();

  const introText: TContentNode = {
    text: t(`Hello ${userFirstName}. What would you like to do today? <br> Ask our AI a question and read more via its sources. Explore innovation themes, or make a request in the Bulletin Boards.`),
  };

  const handleChatClick = () => {
    navigate('https://ai.connectore.org/');
  };
  
  return (
    <ComponentPropsProvider value={{
      FeedEntryCard: {
        direction: 'row',
        descriptionMaxLines: 2,
      },
      PostCard: {
        direction: 'row',
        descriptionMaxLines: 2,
      }
    }}>
    <My.Container className={cls.name(className)}>
      <My.Content>
      <My.Section>
        <My.Intro description={introText.text} />
      </My.Section>

        <My.GptSection>
          <My.GptButton onClick={handleChatClick} entity={entity} label='Ask AI' />
        </My.GptSection>

      </My.Content>
    {children}
    </My.Container>
    </ComponentPropsProvider>
  )
};

ExplorerHomeViewLayout.defaultProps = ExplorerHomeViewLayoutDefaultProps;
ExplorerHomeViewLayout.cls = cls;